import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import Loading from '@features/shared/components/Loading'
import { Path } from '@/routes/paths'
import { useAppNavigation } from '../hooks/useAppNavigation'
import { resolveRoutePath } from '../logic/routeCatalog'
import { useRouteCatalog } from './RouteCatalogContext'
import { findRouteParamSource, type RouteParamFreshness } from '../logic/routeParamSources'
import { settingsQueryKeys } from '@features/settings/api/queryKeys'
import { getUserPreferences } from '@features/settings/api/userPreferences.api'
import { useUpdateOwnPreference } from '@features/settings/hooks/useUpdateOwnPreference'
import { getAllPreferences } from '@features/settings/logic/preferences'
import { useSettingsStore } from '@features/settings/stores/useSettingsStore'
import { useToast } from '@features/shared/components/Toast'
import { useStableCallback } from '@features/shared/hooks/useStableCallback'

const HOME_PAGE_PREFERENCE_KEY = 'home_page'

// Home Pages already reported as gone this session, so an organization's
// stale default warns once, not on every visit.
const reportedGoneHomePages = new Set<string>()

async function ownsHomePage(queryClient: QueryClient) {
  try {
    const preferences = await queryClient.fetchQuery({
      queryKey: settingsQueryKeys.userPreferences,
      queryFn: getUserPreferences,
    })
    const homePage = getAllPreferences(preferences.tabs).find(
      (preference) => preference.preference_key === HOME_PAGE_PREFERENCE_KEY
    )
    return homePage?.resolved_source === 'user_assignment'
  } catch {
    return false
  }
}

/**
 * The app's root address, `/`: forwards to the Home Page preference, or to
 * the AI Assistant when none is set. A Home Page whose item is gone or out of reach,
 * or whose area is switched off, forwards to the nearest page above it and
 * says so; the user's own Home Page moves there, while one an organization or
 * group set is left as set. It goes by the metadata the app already has, so
 * Home opens at once, and checks fresh metadata before treating a page as gone.
 * What can't be checked (the user's modules, or an item's list, didn't load)
 * is never taken as gone.
 */
export default function HomeRedirect() {
  const configurationLoaded = useSettingsStore((state) => state.configurationLoaded)
  const homePage = useSettingsStore((state) => state.homePage)
  const root = useRouteCatalog()
  const { modules, modulesPending, modulesError } = useAppNavigation()
  const queryClient = useQueryClient()
  const { addToast } = useToast()
  const updateOwnPreference = useUpdateOwnPreference()
  const [target, setTarget] = useState<string | null>(null)
  // Until both arrive, every area would look switched off.
  const ready = configurationLoaded && !modulesPending
  // The areas the user may open, as text: the redirect re-runs when they
  // change, not on every render that rebuilds the list, which would resolve
  // (and report a moved Home Page) again.
  const availablePrefixesKey = modules.map((module) => module.pathPrefix).join('|')

  const reportMoved = useStableCallback(async (goneHomePage: string, resolvedPath: string) => {
    if (await ownsHomePage(queryClient)) {
      void updateOwnPreference(
        HOME_PAGE_PREFERENCE_KEY,
        'homePage',
        resolvedPath === Path.home ? null : resolvedPath
      )
      addToast({
        type: 'info',
        title: 'Home page moved',
        message:
          "The page you set as Home isn't available anymore, so Home now opens the nearest page above it.",
      })
      return
    }
    if (reportedGoneHomePages.has(goneHomePage)) return
    reportedGoneHomePages.add(goneHomePage)
    addToast({
      type: 'info',
      title: "Home page isn't available",
      message:
        'The page your organization set as Home is gone, so Home opened the nearest page above it.',
    })
  })

  useEffect(() => {
    if (!ready) return
    if (!homePage) {
      setTarget(Path.aiChat)
      return
    }
    if (modulesError) {
      // Which areas the user may open is unknown, not none: the Home Page
      // opens as set, and says itself if it can't load or isn't open to them.
      setTarget(homePage === Path.home ? Path.aiChat : homePage)
      return
    }
    let cancelled = false
    const availablePrefixes = new Set(availablePrefixesKey.split('|'))
    const resolve = (freshness: RouteParamFreshness) =>
      resolveRoutePath(root, homePage, {
        isTopLevelAvailable: (segment) => availablePrefixes.has(`/${segment}`),
        loadParamValues: async (paramNode, params) => {
          const options = await findRouteParamSource(paramNode.pattern)?.loadOptions(
            params,
            queryClient,
            freshness
          )
          return options ? options.map((option) => option.value) : null
        },
      })
    void resolve('cached')
      // What the app had may be behind a page just made: checked before moving Home.
      .then((resolved) => (resolved.changed ? resolve('fresh') : resolved))
      .then((resolved) => {
        if (cancelled) return
        // Nothing left of the address: the app's own home.
        setTarget(resolved.path === Path.home ? Path.aiChat : resolved.path)
        if (resolved.changed) void reportMoved(homePage, resolved.path)
      })
    return () => {
      cancelled = true
    }
  }, [availablePrefixesKey, homePage, modulesError, queryClient, ready, reportMoved, root])

  if (target === null) return <Loading />
  return <Navigate to={target} replace />
}
